import ArrowBack from '@mui/icons-material/ArrowBack';
import Skeleton from '@mui/material/Skeleton';
import { Logo } from '@openx/components-core/lib/Logo/Logo.tsx';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import { StyledBackground } from '../../components/Background/Background.styled.tsx';
import { getPath } from '../../utils/getQueryRoute.ts';
import { StyledBackButton } from './components/BackButton.styled.tsx';
import { BackgroundIcons } from './components/BackgroundIcons.tsx';
import { StyledPageHeaderContainer } from './components/PageHeaderContainer.styled.tsx';
import { StyledSubtitleTypography } from './components/SubtitleTypography.styled.tsx';
import { StyledTitleContainer } from './components/TitleContainer.styled.tsx';
import { StyledTitleTypography } from './components/TitleTypography.styled.tsx';

type PageHeaderProps = {
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
  isDetailsPage?: boolean;
  children?: React.ReactNode;
  searchParams?: string;
};

export function PageHeader({
  title,
  subtitle,
  isLoading,
  isDetailsPage = false,
  children,
  searchParams,
}: PageHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const path = getPath(searchParams);

  return (
    <StyledPageHeaderContainer isDetailsPage={isDetailsPage}>
      <StyledBackground isHeader>
        <BackgroundIcons />
        <Logo invertColors />
        <StyledTitleContainer isDetailsPage={isDetailsPage}>
          {isLoading && <Skeleton variant="rounded" width={130} height={35} sx={{ marginTop: '7px' }} />}
          {title && (
            <StyledTitleTypography isDetailsPage={isDetailsPage} variant="h1" data-test="page-header-title">
              {title}
            </StyledTitleTypography>
          )}

          {subtitle && (
            <StyledSubtitleTypography variant="h3" data-test="page-header-subtitle">
              {subtitle}
            </StyledSubtitleTypography>
          )}
        </StyledTitleContainer>

        {isDetailsPage && (
          <StyledBackButton
            variant="text"
            startIcon={<ArrowBack />}
            color="inherit"
            onClick={() => {
              navigate(path);
            }}
            data-test="back-button"
          >
            Back to list
          </StyledBackButton>
        )}
      </StyledBackground>

      {children}
    </StyledPageHeaderContainer>
  );
}
